import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { Field, Form } from 'react-final-form';
import Button from '@mui/material/Button';
import { FormControl, InputLabel, Select, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, get, set } from 'lodash';
import { required } from '../../helpers/validators';
import AddressLookup from '../AddressLookup/AddressLookup';
import CountriesSelect from '../CountriesSelect';
import { STRINGS, FORM } from '../../constants';
import { FormInput } from '../FormInput';
import { NetworkHelpers, NormalizerHelpers, Validators } from '../../helpers';
import { StringUtil } from '../../utils';
import ServicePricesModal from '../ServicePricesModal';
import { networksActions } from '../../redux/networksSlice';
import { countriesSelectors } from '../../redux/countriesSlice';

const QuoteForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const countries = useSelector(countriesSelectors.getCountries);
  const [countryISO, setCountryISO] = useState(STRINGS.GB);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [countryObj, setCountryObj] = useState({
    minimumPrice: 2.99,
    countryName: 'United Kingdom',
  });
  const [quoteValues, setQuoteValues] = useState({});
  const [isInternational, setIsInternational] = useState(false);

  const initialValues = useMemo(
    () => ({
      outboundConsignment: {
        deliveryDetails: {
          address: {
            countryCode: STRINGS.GB,
          },
        },
        collectionDetails: {
          address: {
            countryCode: STRINGS.GB,
            town: 'Oldbury',
            county: 'West Midlands',
            postcode: 'B69 4DA',
          },
        },
      },
    }),
    []
  );

  const handleQuoteFormSubmit = useCallback(
    values => {
      let query = `/quotes?${FORM.FIELDS.DESTINATION_COUNTRY.KEY}=${get(
        values,
        FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY
      )}`;
      const destinationAddressKey = values[FORM.FIELDS.ADDRESS_KEY.KEY];
      const weight = values[FORM.FIELDS.WEIGHT.KEY];
      const networkCode = values[FORM.FIELDS.NETWORK_CODE.KEY];

      if (destinationAddressKey) {
        query += `&${FORM.FIELDS.ADDRESS_KEY.KEY}=${destinationAddressKey}`;
      }
      if (weight) {
        query += `&${FORM.FIELDS.WEIGHT.KEY}=${weight}`;
      }
      if (networkCode) {
        query += `&${FORM.FIELDS.NETWORK_CODE.KEY}=${networkCode}`;
      }
      navigate(query);
    },
    [navigate]
  );

  const handleQuickQuoteClick = useCallback(
    async values => {
      const fullData = cloneDeep(values);
      const countryCode = get(
        values,
        FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY
      );
      const town = get(values, FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY);
      const county = get(values, FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY);
      const postcode = get(
        values,
        FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY
      );

      if (countryCode === STRINGS.GB) {
        if (!town) {
          set(fullData, FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY, 'Oldbury');
        }
        if (!county) {
          set(
            fullData,
            FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY,
            'West Midlands'
          );
        }
        if (!postcode) {
          set(
            fullData,
            FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
            'B69 4DA'
          );
        }
      }

      const networksQuery = NetworkHelpers.getNetworkQuery(fullData);
      const networksError = Validators.validateNetworkQuery({
        networksQuery,
        countries,
        deliveryCountryCode: countryCode,
        collectionCountryCode: STRINGS.GB,
      });

      if (!networksError) {
        await dispatch(networksActions.fetchNetworks(networksQuery));
      }

      setQuoteValues(values);
      setServiceModalOpen(true);
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Form
        onSubmit={handleQuoteFormSubmit}
        initialValues={initialValues}
        render={({
          handleSubmit,
          submitting,
          invalid,
          form,
          values,
          errors,
        }) => (
          <Box
            component='form'
            noValidate
            autoComplete='off'
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'flexStart',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flexStart',
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Typography
                  component='h2'
                  variant='subtitle1'
                  sx={{ color: 'primary.contrastText', width: '65px', pt: 1 }}
                >
                  {STRINGS.SEND_PARCEL_HOME_SECTION.SHIP_TO}
                </Typography>
                <Field
                  name={FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY}
                  validate={required}
                  aria-controls='form-destination-country'
                >
                  {props => (
                    <FormControl
                      variant='filled'
                      sx={{
                        minWidth: { md: '200px', xs: '0' },
                        flexGrow: { xs: 1, md: 0 },
                      }}
                      id={'form-control-destination-country'}
                      aria-controls='form-control-destination-country'
                    >
                      <InputLabel
                        htmlFor={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_select`}
                        id={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_label`}
                        aria-controls={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_label`}
                      >
                        {FORM.FIELDS.DESTINATION_COUNTRY.LABEL}
                      </InputLabel>
                      <CountriesSelect
                        component={Select}
                        name={FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY}
                        countries={countries}
                        id={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_select`}
                        size='small'
                        blurOnSelect
                        select
                        autoComplete='off'
                        fullWidth
                        disabled={!countries.length}
                        MenuProps={{
                          PaperProps: { sx: { maxHeight: 500 } },
                        }}
                        onClose={props.input.onBlur}
                        {...props.input}
                        onChange={event => {
                          setCountryISO(event.target.value);
                          props.input.onChange(event.target.value);
                          form.change(FORM.FIELDS.ADDRESS_KEY.KEY, null);
                          setCountryObj(
                            countries.find(
                              country =>
                                country.countryKey === event.target.value
                            )
                          );
                          setIsInternational(event.target.value !== STRINGS.GB);
                        }}
                      />
                    </FormControl>
                  )}
                </Field>
              </Box>
              <FormInput
                name={FORM.FIELDS.WEIGHT.KEY}
                label={FORM.FIELDS.WEIGHT.LABEL}
                fieldProps={{
                  parse: value =>
                    NormalizerHelpers.formatAndConstrainDecimal({
                      maxIntegerLength: 2,
                      maxDecimalLength: 2,
                      minValue: 0.1,
                      value,
                    }),
                  format: StringUtil.formatDecimalString,
                  formatOnBlur: true,
                  validate: value => {
                    // TODO: Update to final version
                    if (Number(value) > 30) {
                      return STRINGS.WEIGHT_MUST_BE_LESS_THAN_30KG;
                    }
                    if (value && Number(value) < 0.1) {
                      return STRINGS.WEIGHT_MUST_BE_MORE_THAN_0KG;
                    }
                  },
                }}
                variant='filled'
                size='small'
                autoComplete='off'
                sx={{
                  minWidth: { md: '150px', xs: '100%' },
                  '& .MuiFormHelperText-root, & .MuiFormHelperText-root.Mui-error':
                    {
                      color: theme => theme.palette.primary.contrastText,
                    },
                  '& .MuiFilledInput-root:hover': {
                    backgroundColor: theme =>
                      theme.palette.primary.contrastText,
                  },
                }}
                helperText={STRINGS.MAX_PARCEL_WEIGHT_$(30)}
                FormHelperTextProps={{
                  sx: {
                    color: 'primary.contrastText',
                  },
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Field
                  name={FORM.FIELDS.ADDRESS_KEY.KEY}
                  maxLength={8}
                  validate={required}
                  aria-controls={FORM.FIELDS.ADDRESS_KEY.KEY}
                  id={FORM.FIELDS.ADDRESS_KEY.KEY}
                >
                  {props => (
                    <AddressLookup
                      {...props.input}
                      autocompleteInputId='address-key'
                      onChange={fullAddress => {
                        // Use unique Address Key value from the full address for the field
                        props.input.onChange(fullAddress?.Key);

                        form.batch(() => {
                          form.change(
                            FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY,
                            fullAddress?.Town
                          );
                          form.change(
                            FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY,
                            fullAddress?.PostalCounty
                          );
                          form.change(
                            FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
                            countryISO === 'US'
                              ? fullAddress.Postcode?.slice(0, 5)
                              : fullAddress.Postcode
                          );
                        });
                      }}
                      label={
                        isInternational
                          ? FORM.FIELDS.ADDRESS_KEY.LABEL_REQUIRED
                          : FORM.FIELDS.ADDRESS_KEY.LABEL
                      }
                      variant='filled'
                      sx={{
                        minWidth: { xs: '100%', md: '300px' },
                        '& .MuiFormHelperText-root, & .MuiFormHelperText-root.Mui-error':
                          {
                            color: theme => theme.palette.primary.contrastText,
                          },
                        '& .MuiFilledInput-root:hover': {
                          backgroundColor: theme =>
                            theme.palette.primary.contrastText,
                        },
                      }}
                      addressKey={props.input.value}
                      countryISO={countryISO}
                      inputSize='small'
                      shouldClearOnChange
                      required={isInternational}
                    />
                  )}
                </Field>
              </Box>
              <Typography
                component='h2'
                variant='subtitle1'
                sx={{ color: 'primary.contrastText', width: '200px', pt: 1 }}
              >
                {STRINGS.SEND_PARCEL_HOME_SECTION.PRICE(
                  Number(countryObj?.minimumPrice || 0).toFixed(2),
                  isInternational
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              <Button
                sx={{
                  mt: 2,
                  alignSelf: 'flex-end',
                  color: 'primary.contrastText',
                  borderColor: 'primary.contrastText',
                  width: { xs: '100%', md: 'auto' },
                }}
                type='button'
                variant='outlined'
                color='primary'
                onClick={() => {
                  handleQuickQuoteClick(values);
                }}
                disabled={
                  !!get(errors, FORM.FIELDS.WEIGHT.KEY) ||
                  (isInternational && invalid)
                }
              >
                {STRINGS.GET_QUOTE}
              </Button>
              <Button
                sx={{
                  alignSelf: 'flex-end',
                  width: { xs: '100%', md: 'auto' },
                }}
                type='submit'
                variant='contained'
                color='secondary'
                disabled={submitting || invalid}
              >
                {STRINGS.SEND_NOW}
              </Button>
            </Box>
          </Box>
        )}
      />
      <ServicePricesModal
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        onSubmit={networkCode => {
          setServiceModalOpen(false);
          handleQuoteFormSubmit({ ...quoteValues, networkCode });
        }}
        values={quoteValues}
        isInternational={isInternational}
      />
    </Box>
  );
};

export default QuoteForm;
